// Backoffice → Raporlama ham-data liste raporları: Satış Detay + Tahsilat Detay.
// Ortak kabuk (ReportShell): üstte kriter seti (tarih aralığı, distribütör, satış
// temsilcisi, müşteri grubu/alt grubu) + Excel export düğmesi, altta ReportGrid.
//
// Tablo eskiden düz bir <table>'dı; sabit raporların ReportGrid altyapısına alındı →
// kolon yönetimi, çoklu sıralama/filtre, gruplama, kayıtlı görünümler, Grafik ve Pivot
// sekmeleri bu iki raporda da çalışıyor. Kolon tanımları ({key,label,num,fmt}) zaten
// ReportGrid'in beklediği şekildeydi, dönüşüm için değiştirilmedi.
import { useState, useEffect, useCallback } from 'react';
import { authHeaders, exportToExcel } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { ReportGrid } from './reports/ReportGrid';
import { DateInput } from './reports/DateInput';
import { MultiSelect } from './reports/MultiSelect';

// ApiResponse / OData sarmalayıcısını açıp düz diziye indirger.
function normalizeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}

const pick = (o, ...keys) => {
  for (const k of keys) if (o?.[k] !== undefined && o?.[k] !== null && o?.[k] !== '') return o[k];
  return undefined;
};

const optId = (o) => pick(o, 'id', 'Id');
const optLabel = (o) =>
  pick(o, 'name', 'Name', 'title', 'Title', 'description', 'Description', 'textId', 'TextId') || '—';
// Lookup listesini MultiSelect'in beklediği {value,label} şekline çevirir.
const asChoices = (arr) => (arr || []).map((o) => ({ value: String(optId(o)), label: optLabel(o) }));

function firstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const fmtDate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('tr-TR');
};
const fmtMoney = (v) =>
  new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v) || 0);
const fmtQty = (v) => new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(Number(v) || 0);

const PAYMENT_STATUS = { 0: 'Açık', 1: 'Onaylı', 2: 'Beklemede', 3: 'İptal' };

// ── Kolon tanımları (key=API alanı, label=başlık, fmt=görüntü/dışa-aktarım biçimi) ──
const SALES_COLUMNS = [
  { key: 'documentDate', label: 'Tarih', fmt: fmtDate },
  { key: 'customerCode', label: 'Müşteri Kodu' },
  { key: 'customerName', label: 'Müşteri' },
  { key: 'representative', label: 'Satış Temsilcisi' },
  { key: 'vendor', label: 'Distribütör' },
  { key: 'productCode', label: 'Ürün Kodu' },
  { key: 'productName', label: 'Ürün' },
  { key: 'quantity', label: 'Miktar', fmt: fmtQty, num: true },
  { key: 'unitPrice', label: 'Birim Fiyat', fmt: fmtMoney, num: true },
  { key: 'discount', label: 'İskonto', fmt: fmtMoney, num: true },
  { key: 'netAmount', label: 'Net Tutar', fmt: fmtMoney, num: true },
];

const COLLECTION_COLUMNS = [
  { key: 'paymentDate', label: 'Tarih', fmt: fmtDate },
  { key: 'receiptNo', label: 'Makbuz No' },
  { key: 'invoiceNo', label: 'Fatura No' },
  { key: 'customerCode', label: 'Müşteri Kodu' },
  { key: 'customerName', label: 'Müşteri' },
  { key: 'representative', label: 'Satış Temsilcisi' },
  { key: 'vendor', label: 'Distribütör' },
  { key: 'paymentType', label: 'Ödeme Türü' },
  { key: 'amount', label: 'Tutar', fmt: fmtMoney, num: true },
  { key: 'bankName', label: 'Banka' },
  { key: 'status', label: 'Durum', fmt: (v) => PAYMENT_STATUS[v] ?? v },
];

const st = {
  page: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: {
    background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #e5e7eb)',
    borderRadius: 16, boxShadow: 'var(--shadow-card)', padding: 14,
  },
  filterRow: { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' },
  field: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 150 },
  label: { fontSize: 12, fontWeight: 800, color: 'var(--text-secondary, #64748b)' },
  input: {
    padding: '8px 10px', borderRadius: 10, border: '1px solid var(--border-color, #e5e7eb)',
    background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #0f172a)', fontWeight: 700, fontSize: 13,
  },
  btn: {
    padding: '9px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
    fontWeight: 800, fontSize: 13,
  },
  btnPrimary: { background: 'var(--accent)', color: '#fff' },
  btnExcel: { background: '#16794c', color: '#fff' },
  tableWrap: { overflowX: 'auto', maxHeight: '64vh', overflowY: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    position: 'sticky', top: 0, background: 'var(--bg-card, #f8fafc)', textAlign: 'left',
    padding: '10px 12px', fontWeight: 800, color: 'var(--text-secondary, #475569)',
    borderBottom: '2px solid var(--border-color, #e5e7eb)', whiteSpace: 'nowrap', zIndex: 1,
  },
  td: { padding: '8px 12px', borderBottom: '1px solid var(--border-color, #eef2f7)', whiteSpace: 'nowrap' },
  empty: { padding: 32, textAlign: 'center', color: 'var(--text-secondary, #94a3b8)', fontWeight: 700 },
  count: { fontSize: 12, fontWeight: 800, color: 'var(--text-secondary, #64748b)' },
};

function Select({ label, value, onChange, options }) {
  return (
    <div style={st.field}>
      <span style={st.label}>{label}</span>
      <select style={st.input} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Tümü</option>
        {options.map((o) => (
          <option key={optId(o)} value={optId(o)}>{optLabel(o)}</option>
        ))}
      </select>
    </div>
  );
}

function ReportShell({ title, endpoint, columns, fileName, reportKey }) {
  usePageMeta(title, 'Ham veri liste raporu');

  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [vendorId, setVendorId] = useState([]);
  const [representativeId, setRepresentativeId] = useState([]);
  const [groupId, setGroupId] = useState([]);
  const [subGroupId, setSubGroupId] = useState([]);

  const [vendors, setVendors] = useState([]);
  const [reps, setReps] = useState([]);
  const [groups, setGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Excel, kullanıcının GÖRDÜĞÜ kümeyi almalı (grid filtresi/sıralaması uygulanmış) —
  // grid görünen satırları buraya bildirir.
  const [visibleRows, setVisibleRows] = useState(null);

  // Kriter dropdown'larını doldur.
  useEffect(() => {
    const headers = authHeaders();
    const load = (url, setter) =>
      fetch(url, { headers }).then((r) => (r.ok ? r.json() : [])).then((j) => setter(normalizeList(j))).catch(() => setter([]));
    load('/api/vendors/Select', setVendors);
    load('/api/representatives/Select', setReps);
    load('/api/customergroups/Select', setGroups);
    load('/api/customersubgroups/Select', setSubGroups);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = new URLSearchParams();
      if (from) p.set('from', from);
      if (to) p.set('to', to);
      if (vendorId.length) p.set('vendorId', vendorId.join(','));
      if (representativeId.length) p.set('representativeId', representativeId.join(','));
      if (groupId.length) p.set('groupId', groupId.join(','));
      if (subGroupId.length) p.set('subGroupId', subGroupId.join(','));
      const r = await fetch(`${endpoint}?${p.toString()}`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`Rapor alınamadı (${r.status})`);
      setRows(normalizeList(await r.json()));
    } catch (e) {
      setError(e.message || 'Hata');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [endpoint, from, to, vendorId, representativeId, groupId, subGroupId]);

  // İlk açılışta varsayılan kriterlerle yükle.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  const onExport = () => {
    const exportCols = columns.map((c) => ({
      field: c.key,
      headerName: c.label,
      valueGetter: ({ row }) => (c.fmt ? c.fmt(row[c.key]) : (row[c.key] ?? '')),
    }));
    exportToExcel(visibleRows ?? rows, exportCols, fileName);
  };

  return (
    <div style={st.page}>
      <div style={st.card}>
        <div style={st.filterRow}>
          <div style={st.field}>
            <span style={st.label}>Başlangıç</span>
            <DateInput value={from} onChange={(v) => setFrom(v)} />
          </div>
          <div style={st.field}>
            <span style={st.label}>Bitiş</span>
            <DateInput value={to} onChange={(v) => setTo(v)} />
          </div>
          <div style={st.field}><span style={st.label}>Distribütör</span><MultiSelect value={vendorId} onChange={setVendorId} options={asChoices(vendors)} /></div>
          <div style={st.field}><span style={st.label}>Satış Temsilcisi</span><MultiSelect value={representativeId} onChange={setRepresentativeId} options={asChoices(reps)} /></div>
          <div style={st.field}><span style={st.label}>Müşteri Grubu</span><MultiSelect value={groupId} onChange={setGroupId} options={asChoices(groups)} /></div>
          <div style={st.field}><span style={st.label}>Müşteri Alt Grubu</span><MultiSelect value={subGroupId} onChange={setSubGroupId} options={asChoices(subGroups)} /></div>
          <button type="button" style={{ ...st.btn, ...st.btnPrimary }} onClick={fetchData} disabled={loading}>
            {loading ? 'Yükleniyor…' : 'Filtrele'}
          </button>
          <button type="button" style={{ ...st.btn, ...st.btnExcel }} onClick={onExport} disabled={rows.length === 0}>
            Excel'e Aktar
          </button>
        </div>
      </div>

      <div style={st.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <strong style={{ fontSize: 15 }}>{title}</strong>
          <span style={st.count}>{rows.length} kayıt{rows.length >= 5000 ? ' (ilk 5000)' : ''}</span>
        </div>
        {error && <div style={{ ...st.empty, color: '#dc2626' }}>{error}</div>}
        {loading && <div style={st.empty}>Yükleniyor…</div>}
        {!loading && !error && rows.length === 0 && <div style={st.empty}>Kriterlere uygun kayıt bulunamadı.</div>}
        {!loading && !error && rows.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 420 }}>
            <ReportGrid
              reportKey={reportKey}
              columns={columns}
              rows={rows}
              onVisibleRowsChange={setVisibleRows}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function SalesDetailReportPage() {
  return (
    <ReportShell
      title="Satış Detay Raporu"
      endpoint="/api/detail-reports/sales"
      columns={SALES_COLUMNS}
      fileName="satis-detay"
      reportKey="sales-detail"
    />
  );
}

export function CollectionDetailReportPage() {
  return (
    <ReportShell
      title="Tahsilat Detay Raporu"
      endpoint="/api/detail-reports/collections"
      columns={COLLECTION_COLUMNS}
      fileName="tahsilat-detay"
      reportKey="collection-detail"
    />
  );
}
